import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Loader2, MapPin, ShieldAlert, Mail, Smartphone } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { useSession } from '../context/SessionContext';

interface AuthScreenProps {
    onSuccess: (identifier: string, role: string, name?: string) => Promise<void> | void;
    roleText: string;
    onBack: () => void;
}

export const AuthScreen: React.FC<AuthScreenProps> = ({ onSuccess, roleText, onBack }) => {
    const { feeConfig } = useSession();
    const [isLogin, setIsLogin] = useState(true);
    const [identifier, setIdentifier] = useState(''); // Email or Phone
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');

    // Dynamic background configuration
    const isVideo = feeConfig.landingBackgroundType === 'video';
    const backgroundUrl = isVideo ? feeConfig.landingVideoUrl : feeConfig.landingImageUrl;

    // Add cache-busting parameter
    const [imageError, setImageError] = useState(false);
    const [mediaKey, setMediaKey] = useState(0);
    const cacheBustedUrl = backgroundUrl ? `${backgroundUrl}${backgroundUrl.includes('?') ? '&' : '?'}t=${Date.now()}` : '';

    const fallbackImage = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1920&h=1080&fit=crop';

    const handleImageError = () => {
        console.error('❌ Failed to load background image:', backgroundUrl);
        setImageError(true);
    };

    // Force reload when background URL or type changes
    React.useEffect(() => {
        setMediaKey(prev => prev + 1);
        setImageError(false);
    }, [backgroundUrl, isVideo]);

    // START: Enhanced Loading State for Async Profile Creation
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showResend, setShowResend] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);

    const handleResendEmail = async () => {
        if (!identifier || !identifier.includes('@')) {
            setError("Please enter a valid email address.");
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const { error: resendError } = await supabase.auth.resend({
                type: 'signup',
                email: identifier.trim(),
                options: {
                    emailRedirectTo: 'https://humhena.com'
                }
            });

            if (resendError) throw resendError;

            setResendSuccess(true);
            setShowResend(false);
            setError("Confirmation email sent! Please check your inbox.");
        } catch (e: any) {
            console.error("Resend Error:", e);
            setError(e.message || "Failed to resend confirmation email.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setResendSuccess(false);
        setShowResend(false);

        try {
            const isEmail = identifier.includes('@');
            let result;

            if (isLogin) {
                // LOGIN
                result = await supabase.auth.signInWithPassword({
                    email: isEmail ? identifier.trim() : undefined,
                    phone: !isEmail ? identifier.trim() : undefined,
                    password: password.trim()
                } as any);
            } else {
                // SIGNUP
                result = await supabase.auth.signUp({
                    email: isEmail ? identifier.trim() : undefined,
                    phone: !isEmail ? identifier.trim() : undefined,
                    password,
                    options: {
                        data: {
                            full_name: name,
                            role: roleText
                        },
                        emailRedirectTo: 'https://humhena.com'
                    }
                } as any);
            }

            if (result.error) throw result.error;

            if (result.data.session) {
                console.log("Auth Success with Session:", result.data.user);
                // ✅ CRITICAL FIX: Await the success callback, but catch failures to stop spinner
                try {
                    await onSuccess(identifier, roleText, !isLogin ? name : undefined);
                    // NOTE: We don't stop loading here because the parent usually unmounts us (redirects)
                } catch (successErr: any) {
                    console.error("Auth success callback failed:", successErr);
                    setError(successErr.message || "Login failed. Please try again.");
                    setIsLoading(false);
                }
            } else if (result.data.user) {
                // User created or returned, but NO SESSION (e.g. Email confirmation required)
                setIsLoading(false); // Stop loading since we are showing a message
                if (!isLogin) {
                    // Switch to login mode and show confirmation message
                    setIsLogin(true);
                    setPassword('');
                    setError("Account Created: Please Check your Email for Confirmation Link");
                    if (isEmail) setShowResend(true);
                } else {
                    setError("Login successful but email not verified. Please check your email for confirmation link.");
                    if (isEmail) setShowResend(true);
                }
            }
        } catch (e: any) {
            console.error("Auth Error:", e);
            let msg = e.message || "Authentication failed.";

            if (e.status === 429 || (msg.includes('after') && msg.includes('seconds'))) {
                const match = msg.match(/after (\d+) seconds/);
                const seconds = match ? match[1] : '60';
                setError(`Security Limit: Too many attempts. Please wait ${seconds}s before trying again.`);
                setIsLoading(false);
                return;
            }

            // Specific check for Supabase Email Rate Limit (Project Level)
            if (msg.includes('over_email_send_rate_limit') || msg.includes('email rate limit exceeded')) {
                setError("⚠️ Project Email Limit Exceeded: The free testing email limit (3/hour) has been reached. Please try again in an hour or ask Admin to configure SMTP.");
                setIsLoading(false);
                return;
            }

            // Standardize Auth API errors
            const isEmail = identifier.includes('@');
            if (msg.includes("Invalid login credentials")) {
                msg = "Invalid email or password. Please try again.";
            } else if (msg.includes("already registered")) {
                if (isEmail) {
                    msg = "This email is already registered. If you haven't verified it yet, you can resend the confirmation link below.";
                    setShowResend(true);
                } else {
                    msg = "User already exists. Please login instead.";
                    setIsLogin(true);
                }
            } else if (msg.includes("Email not confirmed") || msg.includes("email_not_confirmed")) {
                msg = "Please verify your email address to continue. Check your inbox for the confirmation link.";
                setShowResend(true);
            }

            setError(msg);
            setIsLoading(false); // Stop loading only on error
        }
    };

    return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 sm:px-6 md:px-8 py-8 relative overflow-hidden safe-top safe-bottom bg-slate-50 dark:bg-slate-950">
            {/* Flat Solid Background instead of Pop Up */}
            <div className="w-full max-w-sm p-6 sm:p-10 relative z-10 animate-slideUp">
                <div className="mb-8 text-center">
                    <div className="w-24 h-24 flex items-center justify-center mb-6 mx-auto">
                        <img src="/logo.png" alt="Logo" className="w-full h-full object-contain" />
                    </div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-none mb-2">
                        {isLogin ? 'Welcome Back' : 'Create Account'}
                    </h1>
                    <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.3em]">
                        {roleText} Access
                    </p>
                </div>



                <form onSubmit={handleSubmit} className="w-full space-y-4">
                    {error && (
                        <div className="p-4 text-[10px] font-black uppercase tracking-widest rounded-2xl bg-red-50 text-red-600 border border-red-100 flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4 shrink-0" />
                            <span className="break-words">{error}</span>
                        </div>
                    )}

                    {!isLogin && (
                        <div className="bg-white border-2 border-slate-200 rounded-2xl px-5 py-4 focus-within:border-blue-600 transition-all">
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Full Name"
                                required
                                className="w-full bg-transparent border-none p-0 font-bold text-slate-900 focus:ring-0 outline-none placeholder:text-slate-300"
                            />
                        </div>
                    )}

                    <div className="flex items-center gap-3 bg-white border-2 border-slate-200 rounded-2xl px-5 py-4 focus-within:border-blue-600 transition-all">
                        <div className="text-slate-400">
                            {identifier.includes('@') ? <Mail className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                        </div>
                        <input
                            type="text"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            placeholder="Email or Phone"
                            required
                            className="w-full bg-transparent border-none p-0 font-bold text-slate-900 focus:ring-0 outline-none placeholder:text-slate-300"
                        />
                    </div>

                    <div className="bg-white border-2 border-slate-200 rounded-2xl px-5 py-4 focus-within:border-blue-600 transition-all">
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            required
                            minLength={6}
                            className="w-full bg-transparent border-none p-0 font-bold text-slate-900 focus:ring-0 outline-none placeholder:text-slate-300"
                        />
                    </div>

                    <button type="submit" disabled={isLoading} className="group relative w-full h-16 sm:h-18 md:h-20 bg-blue-600 rounded-2xl sm:rounded-[2.5rem] flex items-center justify-center gap-3 sm:gap-4 transition-all active:scale-95 shadow-[0_20px_50px_rgba(37,99,235,0.4)] hover:bg-blue-700 overflow-hidden border border-white/20 mt-4">
                        <span className="text-white text-lg sm:text-xl font-black tracking-tight z-10">
                            {isLoading ? <Loader2 className="animate-spin text-white" /> : (isLogin ? 'Login' : 'Sign Up')}
                        </span>
                        {!isLoading && (
                            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center group-hover:translate-x-1 transition-transform z-10 shadow-lg">
                                <span className="text-blue-600 font-bold text-xl leading-none px-1">→</span>
                            </div>
                        )}
                        {/* Subtle Pulse Layer */}
                        <div className="absolute inset-0 rounded-2xl sm:rounded-[2.5rem] ring-4 ring-white/30 animate-pulse"></div>
                        {/* Glossy highlight effect */}
                        <div className="absolute top-0 -left-[100%] w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:left-[100%] transition-all duration-1000"></div>
                    </button>
                </form>
                <div className="mt-8 flex flex-col items-center justify-center gap-4 w-full">
                    <button onClick={() => setIsLogin(!isLogin)} className="text-sm font-black text-slate-600 dark:text-slate-300 hover:text-blue-600 transition-colors p-2">
                        {isLogin ? 'Create Account' : 'Login'}
                    </button>

                    {showResend && (
                        <button
                            onClick={handleResendEmail}
                            disabled={isLoading}
                            className="w-full py-4 bg-blue-50 text-blue-600 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] border border-blue-100 hover:bg-blue-100 transition-all flex items-center justify-center gap-2"
                        >
                            {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                            Resend Confirmation Email
                        </button>
                    )}

                    {isLogin && (
                        <button
                            onClick={async () => {
                                if (!identifier) {
                                    setError("Please enter your email to reset password.");
                                    return;
                                }
                                setIsLoading(true);
                                try {
                                    const { resetUserPassword } = await import('../services/firestoreService');
                                    await resetUserPassword(identifier);
                                    alert(`Password reset link sent to ${identifier}`);
                                } catch (e: any) {
                                    setError(e.message || "Failed to send reset link.");
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            className="text-sm font-black text-slate-400 dark:text-slate-500 hover:text-red-500 transition-colors p-2"
                        >
                            Forgot Password?
                        </button>
                    )}

                    <button onClick={onBack} className="text-xs font-bold text-slate-400 dark:text-slate-500 hover:text-slate-600 transition-colors p-2 mt-2">
                        Back to Role Selection
                    </button>
                </div>
            </div>
        </div>
    );
};
